import moment from "moment-timezone";
import { TFunction } from "i18next";
import {
  MemberSettings,
  TimeRange,
  WEEKDAY_KEYS,
  WeekdayKey,
} from "../../../../hooks/agenda";
import {
  MIDNIGHT,
  capitalizeFirst,
  rangeLabel,
  weekdayKeyIndex,
} from "./agendaDates";

export interface HoursGroup {
  from: WeekdayKey;
  to: WeekdayKey;
  ranges: TimeRange[];
}

const sameRanges = (a: TimeRange[], b: TimeRange[]) =>
  a.length === b.length &&
  a.every(([start, end], i) => start === b[i][0] && end === b[i][1]);

/** Consecutive days of the week that share the same hours; closed days are left out. */
export const hoursGroups = (hours: MemberSettings["hours"]): HoursGroup[] => {
  const groups: HoursGroup[] = [];
  WEEKDAY_KEYS.forEach((key, index) => {
    const ranges = [...(hours[key] ?? [])].sort((a, b) =>
      a[0].localeCompare(b[0])
    );
    if (ranges.length === 0) return;
    const last = groups[groups.length - 1];
    if (
      last &&
      WEEKDAY_KEYS.indexOf(last.to) === index - 1 &&
      sameRanges(last.ranges, ranges)
    ) {
      last.to = key;
    } else {
      groups.push({ from: key, to: key, ranges });
    }
  });
  return groups;
};

export const listOf = (items: string[], locale: string) => {
  try {
    return new Intl.ListFormat(locale, { type: "conjunction" }).format(items);
  } catch {
    return items.join(", ");
  }
};

const shortDay = (key: WeekdayKey) =>
  moment.localeData().weekdaysShort(moment().day(weekdayKeyIndex(key)));

const clock = (t: TFunction, hhmm: string) =>
  hhmm === MIDNIGHT
    ? (t("agenda.availability.midnight") as string)
    : moment(hhmm, "HH:mm").format("LT");

export const hoursSummary = (
  t: TFunction,
  hours: MemberSettings["hours"],
  locale: string
): string[] =>
  hoursGroups(hours).map(({ from, to, ranges }) =>
    capitalizeFirst(
      t("agenda.availability.line", {
        days:
          from === to
            ? shortDay(from)
            : t("agenda.availability.dayRange", {
                start: shortDay(from),
                end: shortDay(to),
              }),
        hours: listOf(
          ranges.map(([start, end]) =>
            rangeLabel(t, "time", clock(t, start), clock(t, end))
          ),
          locale
        ),
      })
    )
  );
